import type { ThemePreference } from "../../shared/mutators/setUserPreference";
import { useEffect, useEffectEvent, useState } from "react";
import { readColorScheme, type ColorScheme } from "../lib/colorScheme";

function reloadSvgImagesForColorScheme() {
  for (const img of document.images) {
    if (img.src.endsWith(".svg")) img.src = img.src;
  }
}

export function useColorMode({
  bindListeners,
  preference,
  onToggle,
}: {
  bindListeners?: boolean;
  preference?: ThemePreference | null;
  onToggle?: (mode: ColorScheme) => void;
} = {}) {
  const root = document.documentElement;
  const [media] = useState(() =>
    window.matchMedia("(prefers-color-scheme: dark)")
  );

  const resolvePreference = (
    pref: ThemePreference | null | undefined
  ): ColorScheme => {
    if (pref === "light" || pref === "dark") return pref;
    return media.matches ? "dark" : "light";
  };

  // if we set this from `readColorScheme()` immediately, Chrome can
  // respond unreliably, so we wait until the effect
  const [displayedMode, setDisplayedMode] = useState<ColorScheme | null>(null);

  const displayColorMode = (mode: ColorScheme) => {
    const changed = readColorScheme() !== mode;

    // work around bun prod light-dark bugs
    root.style.removeProperty(`--buncss-${mode}`);
    root.style.cssText = `--buncss-${mode === "light" ? "dark" : "light"}:;`;

    root.style.colorScheme = mode;
    if (changed) reloadSvgImagesForColorScheme();

    setDisplayedMode(mode);
  };

  const toggleColorMode = () => {
    const next = readColorScheme() === "dark" ? "light" : "dark";
    displayColorMode(next);
    onToggle?.(next);
  };

  // Match on code, not key: on macOS Option composes a glyph (⇧⌥D → Î).
  const onKeyDown = useEffectEvent((e: KeyboardEvent) => {
    if (!e.shiftKey || !e.altKey || e.metaKey || e.ctrlKey) return;
    if (e.code !== "KeyD") return;
    e.preventDefault();
    toggleColorMode();
  });

  const onMediaChange = useEffectEvent((event: MediaQueryListEvent) => {
    if (preference && preference !== "system") return;
    displayColorMode(event.matches ? "dark" : "light");
  });

  useEffect(() => {
    if (!bindListeners) return;

    // handle system changes
    media.addEventListener("change", onMediaChange);
    return () => media.removeEventListener("change", onMediaChange);
  }, [bindListeners, media]);

  useEffect(() => {
    if (!bindListeners) return;

    // Capture phase so the shortcut still fires while a text editor or the
    // composer (which stop keydown propagation) holds focus.
    root.addEventListener("keydown", onKeyDown, { capture: true });
    return () =>
      root.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [bindListeners, root]);

  const applyPreference = useEffectEvent(() =>
    displayColorMode(resolvePreference(preference))
  );

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: syncs the resolved color mode to the document root colorScheme
    applyPreference();
  }, [preference]);

  return [displayedMode, toggleColorMode] as [ColorScheme, () => void];
}
